import React, { useState } from 'react'
import { LabeledInput } from '@apex/component-library'
import { FaDollarSign } from 'react-icons/fa'
import { useIntl } from 'react-intl'
import { find, includes, startsWith } from 'lodash'
import { FormControl, FormErrorMessage, InputProps } from '@chakra-ui/react'

interface AmountInputProps extends Omit<InputProps, 'size'> {
  rowId: string
  value?: number
  errorRows?: string[]
  handleChange: (newValue?: number) => void
}

const AmountInput = ({
  rowId,
  errorRows,
  handleChange,
  value,
  ...baseProps
}: AmountInputProps) => {
  const formatter = useIntl()
  const [enteringData, setEnteringData] = useState(false)
  const [rawValue, setRawValue] = useState<string>(value ? `${value}` : '')
  const [touched, setTouched] = useState(false)

  const handleFocus = () => {
    setEnteringData(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (includes(['ArrowUp', 'ArrowDown'], e.key)) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleBlur = () => {
    setEnteringData(false)
    const parsedAmount = parseFloat(rawValue.replace(/,/gi, ''))
    const newAmount = isNaN(parsedAmount) ? undefined : parsedAmount
    if (newAmount !== value) {
      handleChange(newAmount)
    }
    setTouched(true)
  }

  const error =
    touched &&
    find(errorRows, errorRow => startsWith(errorRow, rowId))?.replace(
      `${rowId}-`,
      ''
    )
  const hasError = !!error
  const parsedValue = parseFloat(rawValue)
  const formattedValue =
    enteringData || isNaN(parsedValue)
      ? rawValue
      : formatter.formatNumber(parsedValue, {
          style: 'decimal',
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
          minimumIntegerDigits: 1,
        })

  return (
    <FormControl isInvalid={hasError}>
      <LabeledInput
        data-testid="amount-input"
        iconLeft={<FaDollarSign />}
        isInvalid={hasError}
        label=""
        name={`amount-${rowId}`}
        value={formattedValue}
        onChange={e => setRawValue(e.target.value)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        type={enteringData ? 'number' : 'text'}
        textAlign="right"
        {...baseProps}
      />
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  )
}

export default AmountInput
